import { useState } from 'react';
import { Plus, Trash2, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useSiteVisitDefaults, useUpdateSiteVisitDefaults } from '@/hooks/useSettings';
import type { ChecklistStatusOption } from '@/lib/schemas';

const DEFAULT_STATUSES: ChecklistStatusOption[] = [
  { value: 'info_only', label: 'Info Only' },
  { value: 'contractor_action', label: 'Contractor Action' },
  { value: 'architect_action', label: 'Architect Action' },
];

function toValue(label: string): string {
  return label.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
}

export function SiteVisitSettingsTab() {
  const { data, isLoading } = useSiteVisitDefaults();
  const updateDefaults = useUpdateSiteVisitDefaults();

  const [statuses, setStatuses] = useState<ChecklistStatusOption[] | null>(null);
  const [newLabel, setNewLabel] = useState('');

  const currentStatuses = statuses ?? data?.checklist_statuses ?? DEFAULT_STATUSES;

  const handleSave = () => {
    updateDefaults.mutate({ checklist_statuses: currentStatuses });
  };

  const addStatus = () => {
    const trimmed = newLabel.trim();
    if (!trimmed) return;
    const value = toValue(trimmed);
    if (currentStatuses.some((s) => s.value === value)) return;
    setStatuses([...currentStatuses, { value, label: trimmed }]);
    setNewLabel('');
  };

  const removeStatus = (value: string) => {
    setStatuses(currentStatuses.filter((s) => s.value !== value));
  };

  const updateLabel = (value: string, newLabelText: string) => {
    setStatuses(currentStatuses.map((s) => s.value === value ? { ...s, label: newLabelText } : s));
  };

  const resetToDefaults = () => {
    setStatuses(DEFAULT_STATUSES);
  };

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  const isDirty = statuses !== null;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Field Review Settings</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Configure the checklist status options used in field review reports.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Checklist Status Options</CardTitle>
          <CardDescription>
            These options appear in the field review checklist editor and on generated PDF reports.
            The default options use review terminology rather than pass/fail inspection language.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            {currentStatuses.map((s, idx) => (
              <div key={s.value} className="flex items-center gap-2 group">
                <GripVertical className="w-4 h-4 text-muted-foreground/40 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Label</Label>
                    <Input
                      value={s.label}
                      onChange={(e) => updateLabel(s.value, e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Stored value</Label>
                    <Input
                      value={s.value}
                      readOnly
                      className="h-8 text-sm bg-muted text-muted-foreground"
                    />
                  </div>
                </div>
                <button
                  onClick={() => removeStatus(s.value)}
                  className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive flex-shrink-0 mt-4"
                  title="Remove status"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          <div className="flex gap-2 pt-2 border-t">
            <Input
              placeholder="New status label (e.g. Noted)"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              className="h-8 text-sm"
              onKeyDown={(e) => { if (e.key === 'Enter') addStatus(); }}
            />
            <Button size="sm" variant="outline" onClick={addStatus} disabled={!newLabel.trim()}>
              <Plus className="w-4 h-4 mr-1" />
              Add
            </Button>
          </div>

          <div className="flex items-center justify-between pt-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={resetToDefaults}
              className="text-muted-foreground"
            >
              Reset to defaults
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty || updateDefaults.isPending}
            >
              {updateDefaults.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
